<template>
    <mds-label
        :on-dark="onDark"
        :size="size"
        :optional="optional"
        :optional-text="optionalText"
        :required="required"
        :required-text="requiredText"
        :text="label"
        :hidden-label="hiddenLabel"
        :for="inputId"
    >
        <slot name="mds-microcopy-above">
            <mds-microcopy v-if="microcopyAbove" :size="size" :on-dark="onDark" v-html="microcopyAbove" />
        </slot>
        <div v-if="password" :class="$style['mds-input__password-wrapper']">
            <input
                v-bind="$attrs"
                :class="[$style['mds-input'], classObject, sizeClass]"
                v-bind:aria-invalid="error"
                v-on="inputListeners"
                :data-mds-version="version"
                :id="inputId"
                :required="required"
                :type="passwordInputType"
            />
            <mds-button
                :class="$style['mds-input__password-button']"
                type="button"
                variation="icon-only"
                :size="size"
                :icon="showPasswordIcon"
                :on-dark="onDark"
                @click.prevent="isPassword = !isPassword"
                :text="showPasswordText"
            ></mds-button>
        </div>
        <input
            v-else
            v-bind="$attrs"
            :class="[$style['mds-input'], classObject, sizeClass]"
            v-bind:aria-invalid="error"
            v-on="inputListeners"
            :data-mds-version="version"
            :id="inputId"
            :required="required"
        />
        <div v-if="error" :class="$style['mds-input__field-error-wrapper']" role="alert" aria-live="assertive">
            <mds-field-error
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                :size="size"
                v-html="text"
                :on-dark="onDark"
            />
        </div>
        <slot name="mds-microcopy-below">
            <mds-microcopy v-if="microcopyBelow" :size="size" v-html="microcopyBelow" :on-dark="onDark" />
        </slot>
    </mds-label>
</template>
<script>
    import MdsFieldError from '@mds/field-error';
    import MdsLabel from '@mds/label';
    import MdsMicrocopy from '@mds/microcopy';
    import { MdsButton } from '@mds/button';
    import { MdsUniqueId } from '@mds/utils-js';

    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsInput',
        inheritAttrs: false,
        components: {
            MdsLabel,
            MdsFieldError,
            MdsMicrocopy,
            MdsButton,
        },
        mixins: [MdsUniqueId],
        props: {
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, triggers the error state and shows the `errorText` for the input.',
                },
            },
            errorText: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the error text that appears when `error=true` for the input.',
                    support: ['v-html'],
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, visibly hides the input’s label.',
                },
            },
            label: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the label for the input.',
                },
            },
            microcopyAbove: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the input.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the input.',
                    support: ['v-html'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the input label.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe an input as optional.',
                },
            },
            password: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, renders the password variation of the input by setting `type="password"` and adding a button to hide and show the input value.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the input label.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe an input as required.',
                },
            },
            rightAligned: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, right aligns the input’s text.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the input.',
                },
            },
        },
        data() {
            return {
                isPassword: true,
                labels: {
                    showPassword: 'Show password',
                    hidePassword: 'Hide password',
                },
                version: `${name}-${version}`,
            };
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-input--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-input--error']] = this.error;
                obj[this.$style['mds-input--right-aligned']] = this.rightAligned;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            inputListeners() {
                var vm = this;
                return Object.assign({}, this.$listeners, {
                    input(event) {
                        vm.$emit('input', event.target.value, event);
                    },
                });
            },
            passwordInputType() {
                return this.isPassword ? 'password' : 'text';
            },
            showPasswordIcon() {
                return this.isPassword ? 'eye' : 'eye-crossed';
            },
            showPasswordText() {
                return this.isPassword ? this.labels.showPassword : this.labels.hidePassword;
            },
        },
        created() {
            this.inputId = this.$attrs.id ? this.$attrs.id : this.generateUuid('mds-input');
        },
    };
</script>
<style lang="scss" module>
    @import './input.scss';
</style>
